import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DayData, ProjectAnalytics } from 'src/app/models/analytics.model';
import { ProjectManagementService } from 'src/app/services/project-management.service';

interface Bar {
  x: number;
  y: number;
  width: number;
  height: number;
  data: DayData;
}

@Component({
  selector: 'app-project-analytics',
  templateUrl: './project-analytics.component.html',
  styleUrls: ['./project-analytics.component.scss'],
  standalone: false
})
export class ProjectAnalyticsPageComponent implements OnInit {

  projectId: string = null;
  analytics: ProjectAnalytics | null = null;
  loading = true;
  error = false;

  tooltipBar: Bar | null = null;

  readonly chartWidth = 720;
  readonly chartHeight = 180;
  readonly paddingLeft = 36;
  readonly paddingRight = 8;
  readonly paddingTop = 10;
  readonly paddingBottom = 28;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectService: ProjectManagementService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('uuid');
    try {
      this.analytics = await this.projectService.getAnalytics(this.projectId).toPromise();
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  get plotWidth(): number {
    return this.chartWidth - this.paddingLeft - this.paddingRight;
  }

  get plotHeight(): number {
    return this.chartHeight - this.paddingTop - this.paddingBottom;
  }

  get maxCount(): number {
    if (!this.analytics?.daily_views?.length) return 1;
    return Math.max(...this.analytics.daily_views.map(d => d.count), 1);
  }

  get bars(): Bar[] {
    if (!this.analytics?.daily_views?.length) return [];
    const data = this.analytics.daily_views;
    const barWidth = this.plotWidth / data.length;
    const gap = barWidth * 0.18;

    return data.map((d, i) => {
      const barH = (d.count / this.maxCount) * this.plotHeight;
      return {
        x: this.paddingLeft + i * barWidth + gap / 2,
        y: this.paddingTop + this.plotHeight - barH,
        width: barWidth - gap,
        height: Math.max(barH, 1),
        data: d,
      };
    });
  }

  get yAxisTicks(): { value: number; y: number }[] {
    const steps = 4;
    return Array.from({ length: steps + 1 }, (_, i) => ({
      value: Math.round((this.maxCount * i) / steps),
      y: this.paddingTop + this.plotHeight - (i / steps) * this.plotHeight,
    }));
  }

  get xAxisLabels(): { label: string; x: number }[] {
    if (!this.analytics?.daily_views?.length) return [];
    const data = this.analytics.daily_views;
    const barWidth = this.plotWidth / data.length;
    return data
      .filter((_, i) => i % 7 === 0 || i === data.length - 1)
      .map(d => ({
        label: this.formatShort(d.date),
        x: this.paddingLeft + data.indexOf(d) * barWidth + barWidth / 2,
      }));
  }

  formatShort(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  }

  formatFull(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  onBarEnter(bar: Bar) {
    this.tooltipBar = bar;
  }

  onBarLeave() {
    this.tooltipBar = null;
  }

  goBack() {
    this.router.navigate(['/main', 'project', this.projectId]);
  }
}
